/*
============================================================
BLUPRINT RECURRING TASK ENGINE
============================================================

CORE PRINCIPLES
---------------
1. Recurring templates define scheduling rules.
2. Generated tasks are actionable instances.
3. Only one open generated task is allowed per template.
4. Statistics are calculated from schedule expectations,
   not from creating every missed task.
5. Completion-based schedules use actual completion date.
6. Calendar-based schedules ignore completion date.
7. Keep Until Done prevents new task generation while an
   existing generated task remains incomplete.
8. Skip If Missed advances to the current valid occurrence.
9. Seasonal templates generate only during active seasons.
10. Paused templates do not generate tasks.

KEY ADMIN FIELDS
----------------
last_generated_at   - Timestamp of most recently generated task
next_generate_at    - When the next task should be created
last_completed_at   - Timestamp of most recent completion
next_due_date       - Due date for the next generated task
is_paused           - Prevents generation while true
paused_at           - Timestamp when paused

ANCHOR TYPES
------------
calendar   - Fixed schedule based on dates
completion - Next schedule based on completion date

PERSISTENCE TYPES
-----------------
keep_until_done - Existing task blocks future generation
skip_if_missed  - Missed occurrences are skipped

*/

// IMPORTANT:
// BluPrint uses OPERATIONAL DAYS,
// not raw UTC calendar dates.
//
// Dates should always be compared
// using getOperationalDate()
// when evaluating recurrence logic.

import { saveOccurrence } from './features/recurring/engine/occurrenceService'
import { getOperationalDate } from './utils/dateUtils'
import { buildTaskFromOccurrence } from './features/recurring/engine/buildTaskFromOccurrence'
import { generateOccurrences } from './features/recurring/engine/generators/generateOccurrences'
import {
  addOccurrence,
  clearOccurrences,
  getOccurrences
} from './features/recurring/engine/store/occurrenceStore'
import { getNextOccurrenceDate }
    from './features/recurring/engine/getNextOccurrenceDate'
import { sameDay } from './features/recurring/engine/helpers'

// ============================================================
// DATE HELPERS
// ============================================================

export function toDate(value) {

  if (!value) return null

  if (value instanceof Date) {
    return new Date(value)
  }

  // Handle YYYY-MM-DD safely
  if (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {

    const [year, month, day] =
      value.split('-').map(Number)

    return new Date(
      year,
      month - 1,
      day
    )
  }

  return new Date(value)
}

export function formatDate(date) {
  if (!date) return null

  const d = toDate(date)

  const year = d.getFullYear()
  const month = String(
    d.getMonth() + 1
  ).padStart(2, '0')

  const day = String(
    d.getDate()
  ).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export function startOfDay(date) {
  const d = toDate(date)
  if (!d) return null

  const result = new Date(d)
  result.setHours(0, 0, 0, 0)
  return result
}

export function endOfDay(date) {
  const d = toDate(date)
  if (!d) return null

  const result = new Date(d)
  result.setHours(23, 59, 59, 999)

  return result
}

export function isSameOrAfter(dateA, dateB) {
  const a = startOfDay(dateA)
  const b = startOfDay(dateB)

  if (!a || !b) return false

  return a.getTime() >= b.getTime()
}

export function addDays(date, days) {
  const d = startOfDay(date)
  const result = new Date(d)
  result.setDate(result.getDate() + days)
  return result
}

export function addWeeks(date, weeks) {
  return addDays(date, weeks * 7)
}

export function addMonths(date, months) {
  const d = startOfDay(date)

  const result = new Date(d)

  const originalDay =
    result.getDate()

  result.setDate(1)

  result.setMonth(
    result.getMonth() + months
  )

  const daysInMonth = new Date(
    result.getFullYear(),
    result.getMonth() + 1,
    0
  ).getDate()

  result.setDate(
    Math.min(originalDay, daysInMonth)
  )

  return result
}

export function addYears(date, years) {
  const d = startOfDay(date)
  const result = new Date(d)
  result.setFullYear(result.getFullYear() + years)
  return result
}

export function isBefore(dateA, dateB) {
  return (
    startOfDay(dateA).getTime() <
    startOfDay(dateB).getTime()
  )
}

export function isAfter(dateA, dateB) {
  return (
    startOfDay(dateA).getTime() >
    startOfDay(dateB).getTime()
  )
}

export function addInterval(date, value = 1, unit = 'days') {
  switch (unit) {
    case 'days':
      return addDays(date, value)

    case 'weeks':
      return addWeeks(date, value)

    case 'months':
      return addMonths(date, value)

    case 'years':
      return addYears(date, value)

    default:
      return addDays(date, value)
  }
}


export function calculateNextDueDate(
  
  template,
  referenceDate = null
) {

  const recurrenceType = Number(
    template.recurrence_type_id ??
    template.recurrence_type
  )

  const intervalValue = Number(
    template.interval_value ?? 1
  )

  const today = startOfDay(
    referenceDate ||
    getOperationalDate(
      template.timezone || 'UTC',
      template.operational_day_start || 4
    )
  )

  // --------------------------------------------
  // Determine TRUE anchor date
  // --------------------------------------------

  let anchorDate

if (
  template.recurrence_anchor_type === 'completion' &&
  template.last_completed_at
) {

  anchorDate = startOfDay(
    toDate(template.last_completed_at)
  )

} else if (
  template.next_due_date
) {

  anchorDate = startOfDay(
    toDate(template.next_due_date)
  )

} else if (
  template.recurrence_start_date
) {

  anchorDate = startOfDay(
    toDate(template.recurrence_start_date)
  )

} else {

  anchorDate = today
}

  // ==================================================
  // DAILY
  // ==================================================

  if (recurrenceType === 1) {
  
      let nextDate =
      new Date(anchorDate)

    while (
      nextDate < today
    ) {

      nextDate = addDays(
        nextDate,
        intervalValue
      )
    }

    return formatDate(nextDate)
  }

  // ==================================================
  // WEEKLY
  // ==================================================

  if (recurrenceType === 2) {

    let selectedDays =
      template.days_of_week || []

    if (
      !Array.isArray(selectedDays)
    ) {
      selectedDays = []
    }

    if (
      selectedDays.length === 0
    ) {

      selectedDays = [
        anchorDate.getDay()
      ]
    }

    let nextDate =
      startOfDay(today)

    for (
      let i = 0;
      i < 365;
      i++
    ) {

      if (
        selectedDays.includes(
          nextDate.getDay()
        )
      ) {

        return formatDate(
          nextDate
        )
      }

      nextDate =
        addDays(nextDate, 1)
    }
  }

  // ==================================================
  // MONTHLY
  // ==================================================

  if (recurrenceType === 3) {

    let nextDate =
      new Date(anchorDate)

    while (
      nextDate < today
    ) {

      nextDate =
        addMonths(
          nextDate,
          intervalValue
        )
    }

    return formatDate(nextDate)
  }

  // ==================================================
  // YEARLY
  // ==================================================

  if (recurrenceType === 4) {

    let nextDate =
      new Date(anchorDate)

    while (
      nextDate < today
    ) {

      nextDate =
        addYears(
          nextDate,
          intervalValue
        )
    }

    return formatDate(nextDate)
  }

  return formatDate(anchorDate)
}

export function shouldGenerateTask(
  template,
  userSettings,
  existingOpenTask = null
) {

  const today = getOperationalDate(
    userSettings.timezone || 'UTC',
    userSettings.operational_day_start || 4
  )

  // Must be active
  if (!template.is_active) {
    return false
  }

  // Cannot be paused
  if (template.is_paused) {
    return false
  }

  // Must be within recurrence window
  if (template.recurrence_start_date) {

    const startDate = startOfDay(
      template.recurrence_start_date
    )

    if (today < startDate) {
      return false
    }
  }

  if (template.recurrence_end_date) {

    const endDate = startOfDay(
      template.recurrence_end_date
    )

    if (today > endDate) {
      return false
    }
  }

  // Keep Until Done
  if (
    template.persistence_type ===
      'keep_until_done' &&
    existingOpenTask
  ) {
    return false
  }

  // Determine next due date
  const nextDueDate = startOfDay(
  calculateNextDueDate(
    template,
    today
  )
)

  if (!nextDueDate) {
    return false
  }

  // Prevent duplicate generation
  if (template.next_generate_at) {

    const nextGenerate = startOfDay(
      new Date(
        template.next_generate_at
      )
    )

    if (today < nextGenerate) {
      return false
    }
  }

  // Generate if due
  return isSameOrAfter(
    today,
    nextDueDate
  )
}


export function generateTaskFromTemplate(template, dueDate) {
  return {
    name: template.name,
    notes: template.notes || '',

    // Scheduling
    due_date: dueDate,

    // Status
    status_id: template.status_id || 1,

    // Relationships
    project_id: template.project_id || null,
    priority_id: template.priority_id || null,
    task_time_id: template.task_time_id || null,
    context_id: template.context_id || null,
    focus_area_id: template.focus_area_id || null,
    location_id: template.location_id || null,

    // Additional metadata
    time_of_day_id: template.time_of_day_id || null,
    impact_id: template.impact_id || null,
    capacity_id: template.capacity_id || null,
    day_type_id: template.day_type_id || null,

    // Recurring link
    recurring_task_id: template.id,

    // Ownership
    user_id: template.user_id,

    // Default completion state
    is_completed: false,

    created_at: new Date().toISOString()
  }
}


export function updateTemplateAfterGeneration(
    template,
    dueDate
) {

    const nextDueDate =
        getNextOccurrenceDate({
            template,
            currentDate: dueDate
        })

    return {
        last_generated_at:
            new Date().toISOString(),

        next_due_date:
            formatDate(nextDueDate),

        next_generate_at:
            formatDate(nextDueDate)
    }
}

export function updateTemplateAfterCompletion(
  template,
  completionDate = new Date()
) {
  const completion = startOfDay(completionDate)

  // Create a temporary template using the new completion timestamp
  const updatedTemplate = {
    ...template,
    last_completed_at: completion.toISOString()
  }

  // Calculate the next due date based on the updated completion
  const nextDueDate = calculateNextDueDate(
    updatedTemplate,
    completion
  )

  return {
    last_completed_at: completion.toISOString(),
    next_due_date: nextDueDate,
    next_generate_at: nextDueDate
  }
}



export async function runRecurringEngine({
  currentUser,
  recurringTemplates = [],
  existingTasks = [],
  createTask,
  updateTemplate,
  updateTask
}) {
  const userSettings = {
    timezone: currentUser?.timezone || "UTC",
    operational_day_start:
      currentUser?.operational_day_start || 4
  }

  const operationalDate = getOperationalDate(
    userSettings.timezone,
    userSettings.operational_day_start
  )

  clearOccurrences()

  for (const template of recurringTemplates) {

    const existingOpenTask =
      existingTasks.find(task =>
        task.recurring_task_id === template.id &&
        !task.is_completed &&
        !task.is_hidden
      )

    const occurrences = generateOccurrences({
      template,
      existingTasks,
      operationalDate
    })

    for (const occurrence of occurrences) {

      console.log(
        occurrence.generate,
        occurrence.reason,
        occurrence.code
      )

      addOccurrence(occurrence)

      // --------------------------------------------------------
      // Persist the evaluated occurrence.
      //
      // At this point a Task may or may not exist yet.
      // --------------------------------------------------------

      await saveOccurrence(occurrence)

      // --------------------------------------------------------
      // Determine the date represented by this occurrence.
      //
      // ALREADY_GENERATED decisions do not return a dueDate,
      // so for those we use the operational/occurrence date.
      // --------------------------------------------------------

      const occurrenceDateString =
        formatDate(
          occurrence.operationalDate
        )

      const dueDateString =
        occurrence.dueDate
          ? formatDate(occurrence.dueDate)
          : null

      const taskMatchDate =
        dueDateString ||
        occurrenceDateString

      // --------------------------------------------------------
      // See whether a Task already exists for this occurrence.
      //
      // This check happens BEFORE the generate=false exit so
      // ALREADY_GENERATED occurrences can be linked to their
      // existing Task.
      // --------------------------------------------------------

            

      const existingOccurrence =
        existingTasks.find(task =>
          task.recurring_task_id === template.id &&
          !task.is_hidden &&
          sameDay(
            task.due_date,
            occurrence.operationalDate
          )
        )



      if (existingOccurrence) {

        occurrence.taskId =
          existingOccurrence.id

        // The Task already existed.
        // We are linking it to its Occurrence,
        // not creating it now.
        //
        // ALREADY_GENERATED decisions do not carry
        // a dueDate, so recover the actual Task due date.
        if (!occurrence.dueDate) {
          occurrence.dueDate =
            existingOccurrence.due_date
        }

        await saveOccurrence(
          occurrence
        )

        if (!occurrence.generate) {
          continue
        }
      }

      // --------------------------------------------------------
      // If this occurrence does not require a Task, stop here.
      // --------------------------------------------------------

      if (!occurrence.generate) {
        continue
      }

      const dueDate =
        occurrence.dueDate

      if (!dueDate) {
        console.warn(
          "No due date returned for",
          template.name
        )
        continue
      }

      const actualDueDateString =
        formatDate(dueDate)

      // --------------------------------------------------------
      // Persistence rule: skip if the existing open task
      // is older than the occurrence we are generating.
      // --------------------------------------------------------

      if (
        template.persistence_type === "skip_if_missed" &&
        existingOpenTask &&
        existingOpenTask.due_date &&
        formatDate(existingOpenTask.due_date) <
          actualDueDateString
      ) {
        await hideMissedTask(
          existingOpenTask,
          updateTask
        )
      }

      // --------------------------------------------------------
      // If an existing Task was found above, the Occurrence
      // has already been linked.
      //
      // Update the template and continue.
      // --------------------------------------------------------

      if (existingOccurrence) {

        const templateUpdates =
          updateTemplateAfterGeneration(
            template,
            dueDate
          )

        await updateTemplate(
          template.id,
          templateUpdates
        )

        continue
      }

      // --------------------------------------------------------
      // No Task exists yet.
      // Create one from the Occurrence.
      // --------------------------------------------------------

      const newTask =
        buildTaskFromOccurrence(
          occurrence
        )

      const created =
        await createTask(newTask)

      // --------------------------------------------------------
      // Supabase inserts may return an array.
      //
      // Normalize the result so we can reliably obtain
      // the new Task ID.
      // --------------------------------------------------------

      const createdTask =
        Array.isArray(created)
          ? created[0]
          : created

      // --------------------------------------------------------
      // Link the Occurrence to the newly created Task.
      // --------------------------------------------------------

      if (createdTask?.id) {

        occurrence.taskId =
          createdTask.id

        occurrence.taskCreated =
          true

        await saveOccurrence(
          occurrence
        )

      } else {

        console.warn(
          "Task was created but no task ID was returned for occurrence:",
          {
            templateId: template.id,
            templateName: template.name,
            dueDate: actualDueDateString
          }
        )
      }

      // --------------------------------------------------------
      // Keep the in-memory Task list current so later
      // occurrences in this engine run can see it.
      // --------------------------------------------------------

      existingTasks.push({
        ...newTask,
        id: createdTask?.id,
        due_date: actualDueDateString
      })

      const templateUpdates =
        updateTemplateAfterGeneration(
          template,
          dueDate
        )

      await updateTemplate(
        template.id,
        templateUpdates
      )
    }
  }
}

    // --------------------------------------------------------
    // Build Hide Function
    // --------------------------------------------------------
    export async function hideMissedTask(
      task,
      updateTask
    ) {

      await updateTask(task.id, {
        is_hidden: true,
        hidden_at: new Date().toISOString()
      })

        
    }